// src/components/Dashboard/RoleDashboard/RoleExtras/RoleProfile.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "./RoleProfile.css";

// Role-specific fallback images
const FALLBACK_IMAGES = {
    scout: "/images/scout-fallback.png",
    agent: "/images/agent-fallback.png",
    club: "/images/club-fallback.png",
};

function getFallbackForRole(role) {
    const key = (role || "").toLowerCase();
    return FALLBACK_IMAGES[key] || FALLBACK_IMAGES.agent;
}

const VALID_ROLES = ["agent", "scout", "club"];
const isDev =
    typeof process !== "undefined"
        ? process.env.NODE_ENV !== "production"
        : true;

// Used only for EDITING (except scout, which has custom display)
const FIELD_CONFIG = {
    agent: [
        { name: "agencyName", label: "Agency Name", type: "text" },
        { name: "countryOfOperation", label: "Country of Operation", type: "text" },
        { name: "agencyWebsite", label: "Agency Website", type: "text" },
    ],
    scout: [
        { name: "regions", label: "Regions", type: "text" },
        { name: "yearsExperience", label: "Years Experience", type: "number" },

        // 🔽 NEW editable fields
        { name: "currentEmployer", label: "Current Employer", type: "text" },
        { name: "roleAtOrganization", label: "Role", type: "text" },
        { name: "cityBase", label: "City", type: "text" },
        { name: "countryOfOperation", label: "Country of Operation", type: "text" },
        { name: "scoutingExperience", label: "Scouting Experience", type: "text" },
        { name: "scoutingQualifications", label: "Scouting Qualification", type: "text" },

        { name: "website", label: "Website / Portfolio", type: "text" },
    ],
    club: [
        { name: "clubCity", label: "Club City", type: "text" },
        { name: "clubCountry", label: "Club Country", type: "text" },
        { name: "currentLeague", label: "Club League", type: "text" },
        {
            name: "typeOfClub",
            label: "Club Level",
            type: "select",
            options: ["Pro", "Semi-Pro", "Amateur"]
        },

        { name: "clubAgeGroups", label: "Age Groups / Teams Run", type: "text" },
        { name: "clubEmail", label: "Club Email", type: "email" },
        { name: "clubWebsite", label: "Club Website", type: "text" },
    ],
};

function pickImage(obj) {
    return obj?.displayImageUrl || obj?.displayImage || obj?.imageUrl || null;
}





async function fetchJSONWithLog(path, options = {}, opts = {}) {
    const { label = "[RoleProfile] fetch", expectJson = true } = opts;
    const method = options?.method || "GET";
    if (isDev) console.info(`${label} → ${method} ${path}`);

    const res = await apiFetch(path, options).catch((err) => {
        if (isDev) console.error(label, err);
        throw err;
    });

    const ct = res.headers.get("content-type") || "";
    if (isDev) console.info(`${label} ← ${res.status} ${res.statusText} [ct=${ct}]`);

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${label} failed (${res.status}). ${text?.slice(0, 200)}`);
    }

    if (!expectJson) return res;

    if (!ct.includes("application/json")) {
        const text = await res.text().catch(() => "");
        throw new Error(`${label} unexpected content-type ${ct} :: ${text?.slice(0, 200)}`);
    }

    return res.json();
}



function formatDate(date) {
    if (!date) return "Not Available";
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).toString().padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

export default function RoleProfile({ roleOverride }) {
    const navigate = useNavigate();

    const { profileSlug } = useParams();

    // ✅ role comes from App.js route, NOT the URL
    const inferredRole =
        window.location.pathname.startsWith("/agent-profile") ? "agent" :
            window.location.pathname.startsWith("/scout-profile") ? "scout" :
                window.location.pathname.startsWith("/club-profile") ? "club" :
                    "";

    const role = (roleOverride || inferredRole || "").toLowerCase();


    // 🔍 Dev-only sanity log
    if (isDev) {
        console.log("[RoleProfile] roleOverride:", roleOverride);
        console.log("[RoleProfile] role:", role);
        console.log("[RoleProfile] profileSlug:", profileSlug);
    }

    const rootRef = useRef(null);





    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [displayImage, setDisplayImage] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [vsnRating, setVsnRating] = useState(null);
    const [vsnRatingCount, setVsnRatingCount] = useState(null);
    const internalId = profile?.id;




    const appViewerRole =
        typeof window !== "undefined"
            ? (localStorage.getItem("userRole") || "").toLowerCase()
            : "";
    const canEdit = appViewerRole === role;

    const fields = useMemo(() => FIELD_CONFIG[role] || [], [role]);

    // 🔒 Route guards (NO early returns here)
    const isMissingParams = !role || !profileSlug;



    /* -----------------------------------------------------
   Canvas background (prevents flashes during navigation)
------------------------------------------------------*/
    useLayoutEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = "#000"; // RoleProfile canvas (black)
        return () => {
            document.body.style.backgroundColor = prev || "";
        };
    }, []);






    useEffect(() => {
        let ignore = false;

        async function run() {
            // ✅ Only block if params are actually missing
            if (isMissingParams) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");

            try {
                const safeSlug = encodeURIComponent(profileSlug.trim());

                // ✅ Use apiFetch-compatible PATH (no BASE_URL)
                const path = `/api/${role}-profile/${safeSlug}`;

                if (isDev) {
                    console.log("[RoleProfile] FETCHING PUBLIC PROFILE", {
                        role,
                        profileSlug,
                        path,
                    });
                }

                // ✅ fetchJSONWithLog now calls apiFetch internally
                const data = await fetchJSONWithLog(
                    path,
                    {},
                    { label: "[RoleProfile] GET profile" }
                );

                if (ignore) return;

                setProfile(data || {});
                setForm({
                    agencyName: data?.agencyName ?? "",
                    agencyWebsite: data?.agencyWebsite ?? "",
                    countryOfOperation: data?.countryOfOperation ?? "",

                    regions: data?.regionsLeagues ?? data?.regions ?? "",
                    yearsExperience: data?.yearsOfScouting ?? data?.yearsExperience ?? "",
                    website: data?.website ?? "",
                    currentEmployer: data?.currentEmployer ?? "",
                    roleAtOrganization: data?.roleAtOrganization ?? "",
                    cityBase: data?.cityBase ?? "",
                    scoutingExperience: data?.scoutingExperience ?? "",
                    scoutingQualifications: data?.scoutingQualifications ?? "",

                    clubName: data?.clubName ?? "",
                    clubWebsite: data?.clubWebsite ?? "",
                    clubCountry: data?.clubCountry ?? "",
                    currentLeague: data?.currentLeague ?? "",
                    typeOfClub: data?.typeOfClub ?? "",
                    clubEmail: data?.clubEmail ?? "",
                    clubCity: data?.clubCity ?? "",
                    clubAgeGroups: data?.clubAgeGroups ?? "",
                });

                setDisplayImage(pickImage(data) || null);
            } catch (err) {
                if (!ignore) {
                    setError(err.message || "Failed to load profile");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        run();

        return () => {
            ignore = true;
        };
    }, [role, profileSlug, isMissingParams]);





    useEffect(() => {
        let ignore = false;

        async function fetchRating() {
            if (!internalId || !role) return;

            try {
                const path = `/api/role-average-rating/${role}/${internalId}`;


                const data = await fetchJSONWithLog(
                    path,
                    {},
                    { label: "[RoleProfile] GET rating" }
                );


                if (!ignore) {
                    setVsnRating(data?.average ?? null);
                    setVsnRatingCount(
                        data?.count ??
                        data?.ratingCount ??
                        data?.totalRatings ??
                        0
                    );
                }
            } catch (err) {
                if (isDev) {
                    console.warn("[RoleProfile] rating fetch failed", err);
                }
            }
        }

        fetchRating();

        return () => {
            ignore = true;
        };
    }, [internalId, role]);



    const handleDisplayImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file || !internalId) return;

        setUploadingImage(true);

        const formData = new FormData();
        formData.append("image", file);

        // ✅ apiFetch-compatible PATH (no BASE_URL)
        const path = `/api/${role}/${internalId}/display-image/upload`;

        try {
            // ✅ Uses Firebase token automatically via apiFetch()
            // ✅ Do NOT set Content-Type for FormData
            const res = await apiFetch(path, {
                method: "POST",
                body: formData,
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`Upload failed (${res.status}): ${text.slice(0, 200)}`);
            }

            const data = await res.json();

            if (data?.imageUrl) {
                setDisplayImage(data.imageUrl);
                setProfile((p) => ({
                    ...p,
                    displayImageUrl: data.imageUrl,
                }));
            }
        } catch (err) {
            console.error("upload error", err);
        } finally {
            setUploadingImage(false);
        }
    };



    const handleInputChange = (name, value) =>
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));

    const handleSave = async () => {
        let payload = { ...form };

        if (role === "scout") {
            payload.regionsLeagues = form.regions || "";
            payload.yearsOfScouting = form.yearsExperience || "";
        }

        try {
            // ✅ apiFetch-compatible PATH (no BASE_URL)
            const path = `/api/${role}/${internalId}/profile`;


            // ✅ Firebase token is attached automatically by apiFetch()
            const res = await apiFetch(path, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`Save failed (${res.status}): ${text.slice(0, 200)}`);
            }

            const updated = await res.json();
            setProfile((p) => ({ ...p, ...updated }));
            setForm((f) => ({ ...f, ...updated }));
            setIsEditing(false);
        } catch (err) {
            console.warn("Save failed; applying optimistic update", err);
            setProfile((p) => ({ ...p, ...payload }));
            setIsEditing(false);
        }
    };


    const handleCancel = () => {
        setForm({
            agencyName: profile?.agencyName ?? "",
            agencyWebsite: profile?.agencyWebsite ?? "",
            countryOfOperation: profile?.countryOfOperation ?? "",

            regions: profile?.regionsLeagues ?? profile?.regions ?? "",
            yearsExperience: profile?.yearsOfScouting ?? profile?.yearsExperience ?? "",
            website: profile?.website ?? "",

            // 🔽 NEW scout fields
            currentEmployer: profile?.currentEmployer ?? "",
            roleAtOrganization: profile?.roleAtOrganization ?? "",
            cityBase: profile?.cityBase ?? "",
            scoutingExperience: profile?.scoutingExperience ?? "",
            scoutingQualifications: profile?.scoutingQualifications ?? "",

            // Club
            clubName: profile?.clubName ?? "",
            clubWebsite: profile?.clubWebsite ?? "",
            clubCountry: profile?.clubCountry ?? "",
            currentLeague: profile?.currentLeague ?? "",
            typeOfClub: profile?.typeOfClub ?? "",
            clubEmail: profile?.clubEmail ?? "",
            clubCity: profile?.clubCity ?? "",
            clubAgeGroups: profile?.clubAgeGroups ?? "",
        });
        setIsEditing(false);
    };


    const renderStars = (rating) => {
        if (rating == null) return null;
        const full = Math.floor(rating);
        const empty = 5 - full;
        return (
            <>
                <span className="full-stars">{"★".repeat(full)}</span>
                <span className="empty-stars">{"★".repeat(empty)}</span>
            </>
        );
    };





    // ⏳ Normal loading state (only reached when fetch is allowed)
    if (loading) {
        return (
            <div
                ref={rootRef}
                className="role-profile-root"
                style={{ minHeight: "100vh" }}
            >
                <div
                    className="role-profile-container agent-profile-container"
                    style={{ textAlign: "center", padding: "3rem" }}
                >
                    <Loader2 className="animate-spin" />
                    <p>Loading {role} profile...</p>
                </div>
            </div>
        );
    }


    if (error) {
        return (
            <div ref={rootRef} className="role-profile-root" style={{ minHeight: "100vh" }}>
                <div className="role-profile-container agent-profile-container">
                    <p style={{ color: "red" }}>Error: {error}</p>
                </div>
            </div>
        );
    }


    if (!role || !VALID_ROLES.includes(role)) {
        return (
            <div
                ref={rootRef}
                className="role-profile-root"
                style={{ minHeight: "100vh" }}
            >
                <div className="role-profile-container">
                    <h1>Invalid Profile</h1>
                    <p>Role not recognized.</p>
                </div>
            </div>
        );
    }



    // Verification flags from backend
    const isVerified = !!profile?.isVerified;
    const verificationStatus = profile?.verificationStatus || "not_requested";
    const allowResubmission = !!profile?.allowResubmission;
    const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);

    // FULL NAME – ALWAYS computed
    const fullNameValue =
        profile?.fullName ??
        profile?.name ??
        profile?.contactName ??
        profile?.contactFullName ??
        "";

    const dobValue = profile?.dob ? formatDate(profile.dob) : "Not Available";

    // Common left-side rows (for default layout)
    const leftRows = [
        { label: "Full Name", value: fullNameValue },
        { label: "Date of Birth", value: dobValue },
    ];

    const canRequestVerification =
        (role === "agent" || role === "scout" || role === "club") &&
        !isVerified &&
        (verificationStatus === "not_requested" ||
            (verificationStatus === "denied" && allowResubmission));

    // Scout combined display fields
    let scoutDisplayFields = [];
    if (role === "scout") {
        const fullName = fullNameValue || "Not Available";
        const dob = dobValue;

        const employerRoleParts = [];
        if (profile?.currentEmployer) {
            employerRoleParts.push(`Current Employer: ${profile.currentEmployer}`);
        }
        if (profile?.roleAtOrganization) {
            employerRoleParts.push(`Role: ${profile.roleAtOrganization}`);
        }
        const employerRole =
            employerRoleParts.length ? employerRoleParts.join(", ") : "—";

        const cityCountryParts = [];
        if (profile?.cityBase) {
            cityCountryParts.push(`City: ${profile.cityBase}`);
        }
        if (profile?.countryOfOperation) {
            cityCountryParts.push(`Country of Operation: ${profile.countryOfOperation}`);
        }
        const cityCountry =
            cityCountryParts.length ? cityCountryParts.join(", ") : "—";

        const experience = profile?.scoutingExperience || "";
        const quals = profile?.scoutingQualifications || "";
        const experienceQualificationParts = [];
        if (experience) {
            experienceQualificationParts.push(`Scouting Experience: ${experience}`);
        }
        if (quals) {
            experienceQualificationParts.push(`Scouting Qualification: ${quals}`);
        }
        const experienceQualifications =
            experienceQualificationParts.length
                ? experienceQualificationParts.join(", ")
                : "—";

        const website = profile?.website || "—";

        scoutDisplayFields = [
            { label: "Full Name", value: fullName },
            { label: "Date of Birth", value: dob },
            { label: "", value: employerRole },
            { label: "", value: cityCountry },
            { label: "", value: experienceQualifications },
            { label: "Website / Portfolio", value: website },
        ];
    }

    // Club combined display fields
    let clubDisplayFields = [];
    if (role === "club") {
        const clubName = profile?.clubName || "—";
        const city = profile?.clubCity || "";
        const country = profile?.clubCountry || "";
        const league = profile?.currentLeague || "";
        const level = profile?.typeOfClub || "";
        const ageGroups = profile?.clubAgeGroups || "—";
        const email = profile?.clubEmail || "—";
        const website = profile?.clubWebsite || "—";

        const cityCountry =
            city || country
                ? [
                    city ? `Club City: ${city}` : "",
                    country ? `Club Country: ${country}` : "",
                ]
                    .filter(Boolean)
                    .join(", ")
                : "—";

        const leagueLevel =
            league || level
                ? [
                    league ? `Club League: ${league}` : "",
                    level ? `Club Level: ${level}` : "",
                ]
                    .filter(Boolean)
                    .join(", ")
                : "—";

        clubDisplayFields = [
            { label: "Club Name", value: clubName },
            { label: "", value: cityCountry },
            { label: "", value: leagueLevel },
            { label: "Age Groups / Teams Run", value: ageGroups },
            { label: "Club Website", value: website },
        ];
    }

    // Email for its bar
    // ✅ ONLY organization / public contact emails
    const rawEmailValue =
        role === "agent"
            ? profile?.contactEmail || ""
            : role === "scout"
                ? profile?.contactEmail || ""
                : role === "club"
                    ? profile?.clubEmail || ""
                    : "";


    // show real email only when this user can edit & it's the agent role
    const isSelfViewing = canEdit; // already correct
    const showRealEmail = isSelfViewing;

    const emailDisplay = rawEmailValue
        ? showRealEmail
            ? rawEmailValue
            : "Hidden for privacy"
        : "—";

    return (
        <div ref={rootRef} className="role-profile-root" style={{ minHeight: "100vh" }}>
            <div className="role-profile-container agent-profile-container">
                <h1>{`${role} Profile`.replace(/^./, (s) => s.toUpperCase())}</h1>

                {/* banners like old AgentProfile */}
                {!isVerified && verificationStatus === "pending" && (
                    <div className="verification-pending-banner">Verification Pending</div>
                )}

                {isVerified && (
                    <div className="verification-approved-banner">
                        ✅ This {roleLabel} is verified on VSN.
                    </div>
                )}

                <div className="profile-wrapper">
                    <div className="role-profile-inner agent-profile-inner">
                        {/* Left column */}
                        <div
                            className="role-profile-content agent-profile-content"
                            aria-live="polite"
                        >
                            {/* Custom scout layout when NOT editing */}
                            {role === "scout" && !isEditing ? (
                                <>
                                    {scoutDisplayFields.map((r, i) => (
                                        <div key={`scout-${i}`} className="info-box">
                                            {r.label ? (
                                                <>
                                                    <div className="info-label">{r.label}:</div>
                                                    <div className="info-value" title={r.value || ""}>
                                                        {r.label === "Full Name" ? (
                                                            <>
                                                                <span>{r.value}</span>
                                                                {isVerified && (
                                                                    <span
                                                                        className="verification-badge"
                                                                        title={`Verified ${roleLabel}`}
                                                                    >
                                                                        <img
                                                                            src="/twitter-badge.png"
                                                                            alt="Verified Badge"
                                                                            className="twitter-verification-icon"
                                                                        />
                                                                        <span className="verified-text">
                                                                            Verified
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : r.label === "Website / Portfolio" &&
                                                            r.value &&
                                                            r.value !== "—" ? (
                                                            <a
                                                                href={
                                                                    /^https?:\/\//i.test(r.value)
                                                                        ? r.value
                                                                        : `https://${r.value}`
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={r.value || ""}
                                                            >
                                                                {r.value}
                                                            </a>
                                                        ) : (
                                                            r.value
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <div
                                                    className="info-value single-line"
                                                    title={r.value || ""}
                                                >
                                                    {r.value}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </>
                            ) : role === "club" && !isEditing ? (
                                // Custom club layout when NOT editing – 6 rows
                                <>
                                    {clubDisplayFields.map((r, i) => (
                                        <div key={`club-${i}`} className="info-box">
                                            {r.label ? (
                                                <>
                                                    <div className="info-label">{r.label}:</div>
                                                    <div className="info-value" title={r.value || ""}>
                                                        {r.label === "Club Name" ? (
                                                            <>
                                                                <span>{r.value}</span>
                                                                {isVerified && (
                                                                    <span
                                                                        className="verification-badge"
                                                                        title={`Verified ${roleLabel}`}
                                                                    >
                                                                        <img
                                                                            src="/twitter-badge.png"
                                                                            alt="Verified Badge"
                                                                            className="twitter-verification-icon"
                                                                        />
                                                                        <span className="verified-text">
                                                                            Verified
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : r.label === "Club Website" &&
                                                            r.value &&
                                                            r.value !== "—" ? (
                                                            <a
                                                                href={
                                                                    /^https?:\/\//i.test(r.value)
                                                                        ? r.value
                                                                        : `https://${r.value}`
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={r.value || ""}
                                                            >
                                                                {r.value}
                                                            </a>
                                                        ) : (
                                                            r.value
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <div
                                                    className="info-value single-line"
                                                    title={r.value || ""}
                                                >
                                                    {r.value}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </>
                            ) : (
                                // Default / editing layout (agents + fallback)
                                <>
                                    {leftRows.map((r, i) => (
                                        <div key={`common-${i}`} className="info-box">
                                            <div className="info-label">{r.label}:</div>
                                            <div className="info-value" title={r.value || ""}>
                                                {r.label === "Full Name" ? (
                                                    <>
                                                        <span>{r.value || "Not Available"}</span>
                                                        {isVerified && (
                                                            <span
                                                                className="verification-badge"
                                                                title={`Verified ${roleLabel}`}
                                                            >
                                                                <img
                                                                    src="/twitter-badge.png"
                                                                    alt="Verified Badge"
                                                                    className="twitter-verification-icon"
                                                                />
                                                                <span className="verified-text">
                                                                    Verified
                                                                </span>
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    r.value || "Not Available"
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {fields.map((f) => (
                                        <div key={f.name} className="info-box">
                                            <div className="info-label">{f.label}:</div>
                                            <div className="info-value">
                                                {!isEditing ? (
                                                    (f.name === "agencyWebsite" ||
                                                        f.name === "website" ||
                                                        f.name === "clubWebsite") &&
                                                        form[f.name] ? (
                                                        <a
                                                            href={
                                                                /^https?:\/\//i.test(form[f.name])
                                                                    ? form[f.name]
                                                                    : `https://${form[f.name]}`
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={form[f.name] || ""}
                                                        >
                                                            {form[f.name]}
                                                        </a>
                                                    ) : (
                                                        <span title={form[f.name] || ""}>
                                                            {form[f.name] || "—"}
                                                        </span>
                                                    )
                                                ) : f.type === "select" ? (
                                                    <select
                                                        className="text-bar"
                                                        value={form[f.name] ?? ""}
                                                        onChange={(e) =>
                                                            handleInputChange(f.name, e.target.value)
                                                        }
                                                    >
                                                        <option value="">Select...</option>
                                                        {(f.options || []).map((opt) => (
                                                            <option key={opt} value={opt}>
                                                                {opt}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type={f.type}
                                                        className="text-bar"
                                                        value={form[f.name] ?? ""}
                                                        onChange={(e) =>
                                                            handleInputChange(f.name, e.target.value)
                                                        }
                                                    />
                                                )}

                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Email Address bar – always visible, conditionally hidden text */}
                            <div className="info-box">
                                <div className="info-label">Contact Email:</div>
                                <div className="info-value" title={showRealEmail ? rawEmailValue : ""}>
                                    {emailDisplay}
                                </div>
                            </div>

                            <div className="profile-actions" style={{ marginTop: 6 }}>
                                {canEdit ? (
                                    <>
                                        {!isEditing ? (
                                            <button
                                                className="yellow-button"
                                                onClick={() => setIsEditing(true)}
                                            >
                                                Edit
                                            </button>
                                        ) : (
                                            <>
                                                <button className="yellow-button" onClick={handleSave}>
                                                    Save
                                                </button>
                                                <button
                                                    className="yellow-button secondary"
                                                    onClick={handleCancel}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        )}

                                        {canRequestVerification && !isEditing && (
                                            <button
                                                className="yellow-button verify-button"
                                                onClick={() => {
                                                    if (role === "agent") {
                                                        navigate("/agent-verification", {
                                                            state: {
                                                                role: "agent",
                                                                agentId: internalId,
                                                                profileSlug,
                                                            },
                                                        });
                                                    } else if (role === "scout") {
                                                        navigate("/scout-verification", {
                                                            state: {
                                                                role: "scout",
                                                                scoutId: internalId,
                                                                profileSlug,
                                                            },
                                                        });
                                                    } else if (role === "club") {
                                                        navigate("/club-verification", {
                                                            state: {
                                                                role: "club",
                                                                clubId: internalId,
                                                                profileSlug,
                                                            },
                                                        });
                                                    }
                                                }}
                                            >
                                                Request Verification
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <div className="yellow-button--placeholder" aria-hidden />
                                )}
                            </div>
                        </div>


                        {/* Right column photo */}
                        <div className="photo-container photo-column">
                            <div
                                className={`photo-placeholder ${role === "agent" ? "" : "photo-placeholder--tall"
                                    }`}
                            >
                                <img
                                    src={displayImage || getFallbackForRole(role)}
                                    alt={`${role} display`}
                                    className="agent-photo role-photo"
                                    onError={() => {
                                        console.error(`Failed to load ${role} display image:`, displayImage);
                                        setDisplayImage(null);
                                    }}
                                />
                                {canEdit && (
                                    <>
                                        <label
                                            htmlFor="display-file-input"
                                            className="photo-plus-btn"
                                            title={uploadingImage ? "Uploading..." : "Upload new photo"}
                                        >
                                            +
                                        </label>
                                        <input
                                            id="display-file-input"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleDisplayImageUpload}
                                            style={{ display: "none" }}
                                        />
                                    </>
                                )}
                            </div>

                            {role === "agent" && profile?.agentTransfermarktUrl && (
                                <div className="transfermarkt-under-photo">
                                    <a
                                        href={profile.agentTransfermarktUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        [Transfermarkt Profile]
                                    </a>
                                </div>
                            )}


                            {vsnRating != null && (
                                <div className="vsn-rating-display" aria-hidden>
                                    {/* LINE 1: Rating + Number + Stars + Count */}
                                    <div className="vsn-rating-main-line">
                                        <span className="vsn-rating-label">VSN Rating</span>
                                        <span className="vsn-rating-value">
                                            {Number(vsnRating).toFixed(1)}
                                        </span>
                                        <span className="vsn-stars-inline">
                                            {renderStars(vsnRating)}
                                        </span>
                                        {typeof vsnRatingCount === "number" && vsnRatingCount > 0 && (
                                            <span className="vsn-rating-count">
                                                ({vsnRatingCount})
                                            </span>
                                        )}
                                    </div>

                                    {/* LINE 2: Bracketed Explanation */}
                                    <div className="vsn-rating-context">
                                        (VSN Contract Honoring Score)
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}



























